/* eslint no-param-reassign: "error" */
import {
  useEffect,
  useCallback,
} from 'react';
import {
  useParams,
  Navigate,
  useNavigate,
} from 'react-router';
import i18n from 'i18next';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import {
  selection,
} from 'redux-resource-plugins';

import {
  coreActions,
  coreSelectors,
  ProResourceType,
} from '@gymcloud/core';
import {
  getUiState,
} from 'web-selectors';
import {
  getBasePath,
} from 'web-utils';
import {
  uiActions,
} from 'web-actions';
import {
  usePageView,
} from 'web-hooks';

import {
  Flex,
} from 'web-styled';

import {
  Spinner,
  NotFound,
  AddFolder,
  EditFolder,
  EntityFolder,
} from 'web-components';

const getRootKey = (resourceType: ProResourceType) => ({
  exercises: 'exerciseId',
  cooldowns: 'cooldownId',
  warmups: 'warmupId',
  documents: 'documentId',
  programTemplates: 'programId',
  workoutTemplates: 'workoutId',
}[resourceType]);

type Props = {
  resourceType: ProResourceType;
};

const EntityFolderPage = ({ resourceType }: Props) => {
  const navigate = useNavigate();
  const params = useParams();
  const folderId = params.id!;
  const mapState = useCallback(state => ({
    readStatus: getStatus(
      state,
      [
        'folders.requests.fetchLibrary.status',
        `folders.meta[${folderId}].readStatus`,
      ],
      false,
    ),
    updateStatus: getStatus(
      state,
      [
        'folders.requests.copyItems.status',
        `${resourceType}.requests.duplicateEntity.status`,
      ],
      false,
    ),
    items: coreSelectors.getChildrenFolderById(
      resourceType,
      Number(folderId),
    )(state),
    folder: coreSelectors.getResourceById(
      'folders',
      folderId,
    )(state),
    breadcrumbs: coreSelectors.getBreadcrumbs(
      resourceType,
      Number(folderId),
    )(state),
    searchOptions: coreSelectors.getEntitySearchOptions(
      resourceType,
      'library',
    )(state),
    folderRoots: coreSelectors.getFolderRoots(state),
    ...getUiState([
      'addFolderModal',
      'openedTooltips',
      'renameFolderModal',
    ])(state),
  }), [folderId]);

  const {
    items,
    folder,
    readStatus,
    updateStatus,
    breadcrumbs,
    folderRoots,
    searchOptions,
    addFolderModal,
    renameFolderModal,
    openedTooltips,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  // clear after leaving the page
  useEffect(() => () => {
    dispatch(
      selection.clearSelectedResources('folders'),
    );
    dispatch(
      selection.clearSelectedResources(resourceType),
    );
  }, []);

  usePageView({
    eventKey: 'viewed_folder',
    attrs: folder,
    enabled: readStatus.succeeded && folder,
  });

  const handleSelect = (item, selected) => {
    const type = item.parent_id ? 'folders' : resourceType;
    dispatch(selected
      ? selection.deselectResources(type, [item.id])
      : selection.selectResources(type, [item.id]));
  };

  // items can contain several types of resource,
  // so we can't dispatch together
  const handleSelectAll = () => {
    const notRoot = items.filter(item => !item.is_root);
    const every = notRoot.every(item => item.selected);
    notRoot.forEach(item => handleSelect(item, every));
  };

  const handleChangeName = (item, newName) => {
    const type = item.parent_id ? 'folders' : resourceType;
    dispatch(
      coreActions.updateResources({
        resources: [{
          id: item.id,
          name: newName,
        }],
        resourceType: type,
      }),
    );
    dispatch(
      coreActions.requestUpdateEntity({
        id: item.id,
        payload: {
          name: newName,
        },
        endNotify: true,
        resourceType: type,
      }),
    );
  };

  const handleDeleteItems = () => {
    const selected = items.filter(item => item.selected);
    const onConfirmAction = coreActions.requestDeleteItems({
      resources: {
        folders: selected.filter(item => !!item.parent_id).map(item => item.id),
        [resourceType]: selected.filter(item => !!item.folder_id).map(item => item.id),
      },
    });
    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete item(s)',
          bodyText: 'Are you sure you want to delete selected items?',
          confirmText: 'Delete',
        },
        onConfirmAction,
      }),
    );
  };

  const removeEntityIdFromTooltips = (tooltip: any) => {
    if (tooltip) {
      const newList = openedTooltips.filter(el => el.key !== tooltip.key);
      if (tooltip.nextKey) {
        newList.push({ key: tooltip.nextKey });
      }

      dispatch(
        uiActions.setUiState({
          openedTooltips: newList,
        }),
      );
    }
  };

  const basePath = getBasePath(resourceType);
  const root = folderRoots[getRootKey(resourceType)]
  if (folderId === '0' && root) {
    return (
      <Navigate to={`${basePath}_folder/${root}`} replace />
    )
  }

  return (
    <Flex fillSpace>
      {(readStatus.succeeded && folder) && (
        <EntityFolder
          items={items}
          basePath={basePath}
          breadcrumbs={breadcrumbs}
          folderRoots={folderRoots}
          searchOptions={searchOptions}
          folder={folder}
          resourceType={resourceType}
          openedTooltips={openedTooltips}
          onSelect={(item) => {
            handleSelect(item, item.selected);
          }}
          onSelectAll={handleSelectAll}
          onChangeName={handleChangeName}
          onRequestAddEntity={() => {
            const tooltipKey = `add_${resourceType}`;
            const addTooltip = openedTooltips.find(el => el.key === tooltipKey);
            let buttonTooltip;
            let nextTooltips: any = [];
            if (addTooltip) {
              removeEntityIdFromTooltips(addTooltip);
              switch (tooltipKey) {
                case 'add_exercises':
                  nextTooltips = [{ key: 'assign_video', nextKey: 'enter_description' }];
                  buttonTooltip = i18n.t(`tooltip.add.modal.button.${resourceType}`);
                  break;
                case 'add_workoutTemplates':
                  buttonTooltip = i18n.t(`tooltip.add.modal.button.${resourceType}`);
                  break;
                default:
              }
            }

            dispatch(
              uiActions.setUiState({
                addEntityModal: {
                  isOpen: true,
                  buttonLabel: i18n.t(`add.${resourceType}`),
                  nextTooltips,
                  buttonTooltip,
                  meta: {
                    folder_id: folderId,
                    is_warmup: resourceType === 'warmups',
                    is_visible: true,
                    is_cooldown: resourceType === 'cooldowns',
                  },
                  resourceType,
                },
              }),
            );
          }}
          onRequestAddFolder={() => {
            dispatch(uiActions.setUiState({ addFolderModal: true }));
          }}
          onRequestDeleteFolder={() => {
            const onConfirm = () => {
              navigate(-1);
              dispatch(
                coreActions.requestDeleteEntity({
                  id: folderId,
                  resourceType: 'folders',
                }),
              );
            };
            dispatch(
              uiActions.confirmModal({
                modal: {
                  title: 'Delete folder?',
                  bodyText: 'Are you sure you want to delete this folder?',
                  confirmText: 'Delete',
                },
                onConfirm,
              }),
            );
          }}
          onRequestRenameFolder={() => {
            dispatch(
              uiActions.setUiState({
                renameFolderModal: true,
              }),
            );
          }}
          onRequestDeleteEntity={handleDeleteItems}
          onRemoveEntityTooltip={removeEntityIdFromTooltips}
          onRequestMoveEntity={() => {
            const onSelect = (values, actions) => {
              dispatch(
                coreActions.requestMoveToFolder({
                  items: items.filter(item => item.selected),
                  targetId: values.folderId,
                  resourceType,
                  callbacks: {
                    ...actions,
                    onSuccess: () => {
                      dispatch(
                        uiActions.calcelMoveEntityModal(),
                      );
                    },
                  },
                }),
              );
            };

            dispatch(
              uiActions.moveEntityModal({
                modal: {
                  helper: i18n.t('select.folder.to.move.selected', { type: `$t(${resourceType})` }),
                  isOpen: true,
                  folderName: breadcrumbs[0].name,
                },
                onSelect,
              }),
            );
          }}
          onRequestCopyEntity={() => {
            dispatch(
              selection.clearSelectedResources('folders'),
            );
            dispatch(
              selection.clearSelectedResources(resourceType),
            );
            dispatch(
              coreActions.requestCopyItems({
                items: items.filter(item => item.selected),
                folderId,
                resourceType,
              }),
            );
          }}
          onRequestDuplicateEntity={(id) => {
            dispatch(
              coreActions.requestDuplicateEntity({
                ids: [id],
                folderIds: [folderId],
                resourceType,
              }),
            );
          }}
          onRequestCreateEntity={(name) => {
            dispatch(
              coreActions.requestAddEntity({
                payload: {
                  name,
                  folder_id: folderId,
                },
                list: 'library',
                resourceType,
                callbacks: {
                  onSuccess: (entity: any) => {
                    setTimeout(() => {
                      const elm = document.getElementById(entity.id);
                      elm?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      });
                      elm?.classList.add('blinker');
                    }, 100);
                  },
                },
              }),
            );
          }}
        />
      )}

      {readStatus.pending && (
        <Spinner message="Loading Library..." />
      )}

      {updateStatus.pending && (
        <Spinner over />
      )}

      {(!readStatus.pending && (readStatus.failed || !folder)) && (
        <NotFound title={`Not Found (${folderId})`} />
      )}

      <AddFolder
        isOpen={addFolderModal}
        onRequestClose={() => {
          dispatch(uiActions.setUiState({ addFolderModal: false }));
        }}
        onSubmit={(values, actions) => {
          dispatch(
            coreActions.requestAddFolder({
              payload: {
                ...values,
                parent_id: folderId,
              },
              callbacks: {
                ...actions,
                onSuccess: () => {
                  dispatch(
                    uiActions.setUiState({
                      addFolderModal: false,
                    }),
                  );
                },
              },
            }),
          );
        }}
      />
      <EditFolder
        initialValues={{
          name: folder?.name || '',
        }}
        isOpen={renameFolderModal}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              renameFolderModal: false,
            }),
          );
        }}
        onSubmit={(values, actions) => {
          dispatch(
            coreActions.requestUpdateFolder({
              id: folderId,
              payload: values,
              callbacks: {
                ...actions,
                onSuccess: () => {
                  dispatch(
                    uiActions.setUiState({
                      renameFolderModal: false,
                    }),
                  );
                },
              },
            }),
          );
        }}
      />
    </Flex>
  );
};

export default EntityFolderPage;
